import {MFA_DEVICE_HEADER, MFA_SETUP_PATH, MFA_VERIFY_PATH, readDeviceToken} from '~/utils/mfa';

import { useAuthService } from "~/composables/useAuthService";
import { useRuntimeConfig } from "#app";

/**
 * The user's IANA zone, preferring the cookie the timezone plugin writes — it
 * has a jstz fallback chain for browsers whose `Intl` lies — and falling back to
 * `Intl` directly.
 *
 * Both reads are guarded: `httpRequest` is called from event handlers and
 * timers, where `useCookie` has no Nuxt instance to attach to and throws. A
 * failed lookup must cost a header, never a request.
 */
function detectedTimezone() {
  try {
    const fromCookie = useCookie('timezone').value;
    if (fromCookie) return fromCookie;
  } catch {
    // No Nuxt context here — fall through to Intl.
  }
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/** The app locale, guarded for the same reason as the timezone above. */
function detectedLocale() {
  try {
    const fromI18n = useNuxtApp().$i18n?.locale?.value;
    if (fromI18n) return fromI18n;
  } catch {
    // No Nuxt context here — fall through to the browser.
  }
  return typeof navigator !== 'undefined' ? navigator.language || null : null;
}

export function useHttpRequest() {
  const config = useRuntimeConfig().public.grantThomasNuxt;
  const { accessToken, impersonatingAccessToken, logout } = useAuthService();
  const { setAlertMessage } = useAlertService();
  const { stringifyQuery, flattenQuery } = useCrudConverters();

  const httpRequest = async (endpoint, data) => {
    var headers = {
      Accept: 'application/json',
    };

    if (endpoint.requiresAuth) {
      if (impersonatingAccessToken.value != null && impersonatingAccessToken.value.length > 10) {
        headers.Authorization = "Bearer " + impersonatingAccessToken.value;
      } else {
        headers.Authorization = "Bearer " + accessToken.value;
      }
    }

    // The timezone plugin has always detected this and written it to a cookie,
    // but nothing ever sent it and the API reads no cookies — so a web-only user
    // had NO timezone stored, and every fixture email told them "timezone not
    // detected" above a UTC time that was usually an hour or more out. The
    // mobile app has sent this header all along; the website simply never did.
    // Never while impersonating: the API would store the admin's zone as the
    // member's. The server refuses it too (Helpers.Impersonation); this keeps the
    // admin's zone out of the request altogether, as updateAuthHeaders already does.
    // A browser remembered after a two-step sign-in says so on every call
    // (MFA_PLAN.md); never while impersonating, the device is the admin's.
    if (endpoint.requiresAuth && process.client && !impersonatingAccessToken.value) {
      const device = readDeviceToken();
      if (device) headers[MFA_DEVICE_HEADER] = device;
    }

    const timezone = detectedTimezone();
    if (timezone && !impersonatingAccessToken.value) {
      headers['X-Timezone-Identifier'] = timezone;
    }

    // Read by the API in one place: the payments prompts take the device region
    // from it ("en-US" is US) to pick the round fee's currency when the club has
    // no country (PaymentPromptsController, Release 1c). Dates do not use it.
    const locale = detectedLocale();
    if (locale) {
      headers['Accept-Language'] = locale;
    }

    try {
      let url = `${config.apiBaseUrl}${endpoint.path}`;
      let options = {
        method: endpoint.method.toUpperCase(),
        headers: headers,
      };

      // A delete may carry a body too (removing a sign-in method sends the code that proves it's you).
      if (endpoint.method === 'post' || endpoint.method === 'put' || (endpoint.method === 'delete' && data)) {
        options.body = JSON.stringify(data);
        headers['Content-Type'] = 'application/json';
      }

      if (endpoint.method === 'get' && data && Object.keys(data).length > 0) {
        const queryString = stringifyQuery(flattenQuery(data));
        url += `?${queryString}`;
      }

      const response = await fetch(url, options);

      if (!response.ok) {
        throw response;
      }

      const responseData = await response.json();

      if (response.headers.get('user-message')) {
        setAlertMessage(response.headers.get('user-message'));
      }

      return responseData;
    } catch (e) {
      // A 401 means "your session is gone" ONLY when we sent one. Anonymous
      // calls (requiresAuth: false — the no-login pay page, public reads) can
      // 401 for their own reasons, and logging the visitor out on those looped
      // /pay/charge/{token} through login forever (prod, Sep 2026).
      if (e.status === 401 && endpoint.requiresAuth) {
        await logout();
      }
      // The API holds an account that must use two-step sign-in at its
      // endpoints (403 mfa_required, MFA_PLAN.md): put the person on the step.
      if (e.status === 403 && process.client && typeof e.clone === 'function') {
        try {
          const body = await e.clone().json();
          if (body?.code === 'mfa_required') {
            const step = body.enrolled ? MFA_VERIFY_PATH : MFA_SETUP_PATH;
            const here = window.location.pathname + window.location.search;
            // The one-time sign-in link page sends the person on itself, and its
            // address must never be the way back (the link is spent by then).
            if (!here.startsWith('/security/') && !here.startsWith('/login-with-token')) {
              await navigateTo(step + '?redirect=' + encodeURIComponent(here));
            }
          }
        } catch { /* not ours */ }
      }
      // Check if error is a Response object with json method
      if (e && typeof e.json === 'function') {
        let responseData;
        try {
          responseData = await e.json();
        } catch (jsonError) {
          // If json() fails, throw the original error
          throw e;
        }
        throw responseData;
      }
      // If not a Response object, throw as is
      throw e;
    }
  };

  return { httpRequest };
}


